import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { PageHeader, Card, CardHeader, Button, Field, Input, Textarea, Select, ErrorText } from "../../components/ui";
import type { ClientRow, Page } from "../../lib/types";

interface Service { id: string; code: string; name: string }

export function CrmCaseCreate() {
  const navigate = useNavigate();
  const clientsQ = useApi<Page<ClientRow>>("/api/crm/clients?limit=100");
  const servicesQ = useApi<{ data: Service[] }>("/api/crm/services");

  const [f, setF] = useState({ clientId: "", serviceId: "", make: "", model: "", vin: "", year: "", mileageKm: "", price: "", listingUrl: "", notes: "" });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((s) => ({ ...s, [k]: e.target.value }));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const vehicle = await api.post<{ id: string }>("/api/crm/vehicles", {
        make: f.make,
        model: f.model,
        vin: f.vin || undefined,
        year: f.year ? Number(f.year) : undefined,
        mileageKm: f.mileageKm ? Number(f.mileageKm) : undefined,
        listedPriceMinor: f.price ? Math.round(Number(f.price) * 100) : undefined,
        listingUrl: f.listingUrl.trim() || undefined,
      });
      const created = await api.post<{ id: string }>("/api/crm/cases", {
        clientId: f.clientId,
        vehicleId: vehicle.id,
        serviceId: f.serviceId,
        notes: f.notes.trim() || undefined,
      });
      void navigate(`/cases/${created.id}`);
    } catch {
      setError("Revisa los datos: cliente, servicio y vehículo son obligatorios.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title="Nuevo expediente" subtitle="Cliente, servicio y vehículo a inspeccionar (Alemania)" />
      <Card className="max-w-2xl">
        <CardHeader title="Datos del expediente" />
        <form onSubmit={(e) => void submit(e)} className="grid gap-4 p-5 sm:grid-cols-2">
          {error && <div className="sm:col-span-2"><ErrorText>{error}</ErrorText></div>}
          <Field label="Cliente">
            <Select value={f.clientId} onChange={set("clientId")} required>
              <option value="">Selecciona…</option>
              {clientsQ.data?.data.map((c) => <option key={c.id} value={c.id}>{c.displayName}</option>)}
            </Select>
          </Field>
          <Field label="Servicio">
            <Select value={f.serviceId} onChange={set("serviceId")} required>
              <option value="">Selecciona…</option>
              {servicesQ.data?.data.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </Field>

          <div className="sm:col-span-2 mt-1 text-xs font-semibold uppercase tracking-wide text-muted">Vehículo</div>
          <Field label="Marca"><Input value={f.make} onChange={set("make")} required /></Field>
          <Field label="Modelo"><Input value={f.model} onChange={set("model")} required /></Field>
          <Field label="VIN" hint="Opcional"><Input value={f.vin} onChange={(e) => setF((s) => ({ ...s, vin: e.target.value.toUpperCase() }))} maxLength={17} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Año"><Input type="number" value={f.year} onChange={set("year")} /></Field>
            <Field label="Km"><Input type="number" value={f.mileageKm} onChange={set("mileageKm")} /></Field>
          </div>
          <Field label="Precio del vehículo (€)" hint="Opcional"><Input type="number" step="0.01" value={f.price} onChange={set("price")} placeholder="18500" /></Field>
          <Field label="URL del anuncio" hint="Opcional"><Input type="url" value={f.listingUrl} onChange={set("listingUrl")} placeholder="https://mobile.de/…" /></Field>

          <div className="sm:col-span-2">
            <Field label="Notas" hint="Opcional">
              <Textarea value={f.notes} onChange={set("notes")} rows={2} placeholder="Cualquier detalle relevante del expediente…" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={busy || !f.clientId || !f.serviceId || !f.make || !f.model}>{busy ? "Creando…" : "Crear expediente"}</Button>
          </div>
        </form>
      </Card>
    </>
  );
}
